/**
 * netprobe — проба двух адресов панели с телефона человека (26 сен 2026).
 *
 * Зачем. У части людей мини-апп не открывается вовсе, и до нас не долетает ни байта:
 * ответ с российского адреса гибнет по дороге в часть зарубежных сетей, где стоят
 * VPN-серверы (замер 26 сен: мост и ya.ru одинаково глухи из LeaseWeb SG, Solid Systems
 * LT, HOSTKEY FI), а прямой путь из РФ к Амстердаму исторически душат. Какой путь выберет
 * телефон, решают невидимые настройки VPN-приложения. Сервер провал не видит — поэтому
 * смотрим с другой стороны: у кого панель ОТКРЫЛАСЬ, тот раз в сутки тихо стучится в ОБА
 * адреса (маленький ответ и 64 КБ — больше порога, на котором режут ответы). Отсюда доля
 * людей, видящих только мост / только Амстердам / оба, и сеть каждого пути (адрес пишет
 * сервер). Смотреть: `padla look netprobe`.
 *
 * XMLHttpRequest, а не fetch: fetch перехвачен гонкой мостов (edge-fetch) и при сбое
 * сам переехал бы на другой адрес — проба мерила бы не тот путь.
 * Никогда не бросает и не мешает человеку: после старта, в простое, раз в сутки (П5).
 */

import { EDGE_NAMES, PANEL_EDGES, isGoOrigin, pinnedBridgeOf, servedBridge, type BridgeId } from './edge-fetch';
import { sendRum } from './rum';

export type ProbeVia = 'ru' | 'nl';
/** limited — сервер отказал по лимиту частоты (общий адрес оператора): путь жив, мерить нечего. */
export type ProbeOutcome = 'ok' | 'timeout' | 'network' | 'short' | 'http' | 'limited';

export interface ProbeLeg {
  ok: boolean;
  ms: number;
  err?: Exclude<ProbeOutcome, 'ok'>;
}

export interface ProbeResult {
  via: ProbeVia;
  s: ProbeLeg;
  l: ProbeLeg;
}

/**
 * Адрес → метка пути: российский мост и Амстердам напрямую. С 27 сен 2026 российский мост — наш padla-ru
 * (`ru.go.padla.pro`), не старый ru.panel на чужом kubik (его сертификат кончается 6 окт): прибор меряет путь,
 * которым людей реально ведёт умный вход.
 */
export const PROBE_HOSTS: Record<ProbeVia, string> = { ru: EDGE_NAMES.ru, nl: PANEL_EDGES[1] };

export const NETPROBE_LARGE_BYTES = 65_536;
const SMALL_TIMEOUT_MS = 10_000;
const LARGE_TIMEOUT_MS = 20_000;
const STORAGE_KEY = 'padla_netprobe_at';
const EVERY_MS = 24 * 60 * 60 * 1000;

/** Откуда открыта панель: ru | nl; вне прод-адресов — null (стенды, localhost). */
/**
 * Точка входа, через которую пришла страница: российский мост → `ru`, Амстердам → `nl`. Старые адреса — по имени;
 * умный вход — по мосту в имени (закреплённое) или по мосту, который назвался заголовком `X-Padla-Bridge`.
 * До 27 сен умный вход сюда не узнавался, и с переключения всех на go.padla.pro проба не бежала ни у кого.
 */
export function entryOf(origin: string, served: BridgeId | null = servedBridge()): ProbeVia | null {
  if (origin === PANEL_EDGES[0] || origin === EDGE_NAMES.ru) return 'ru';
  if (origin === PANEL_EDGES[1] || origin === EDGE_NAMES.ams) return 'nl';
  if (!isGoOrigin(origin)) return null;
  const bridge = pinnedBridgeOf(origin) ?? served;
  return bridge === 'ru' ? 'ru' : bridge === 'ams' ? 'nl' : null;
}

export type XhrFactory = () => XMLHttpRequest;

/** Один запрос пробы. Исход — всегда (никогда не бросает). */
export function probeOnce(
  url: string,
  expectBytes: number | null,
  timeoutMs: number,
  makeXhr: XhrFactory = () => new XMLHttpRequest(),
  now: () => number = () => performance.now(),
): Promise<ProbeLeg> {
  return new Promise((resolve) => {
    const t0 = now();
    const done = (leg: Omit<ProbeLeg, 'ms'>): void => resolve({ ...leg, ms: Math.round(now() - t0) });
    try {
      const xhr = makeXhr();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';
      xhr.timeout = timeoutMs;
      xhr.onload = () => {
        if (xhr.status === 429) return done({ ok: false, err: 'limited' });
        if (xhr.status !== 200) return done({ ok: false, err: 'http' });
        const got = xhr.response instanceof ArrayBuffer ? xhr.response.byteLength : 0;
        if (expectBytes !== null && got !== expectBytes) return done({ ok: false, err: 'short' });
        done({ ok: true });
      };
      xhr.ontimeout = () => done({ ok: false, err: 'timeout' });
      xhr.onerror = () => done({ ok: false, err: 'network' });
      xhr.send();
    } catch {
      done({ ok: false, err: 'network' });
    }
  });
}

/** Проба одного пути: маленький ответ, затем 64 КБ. */
export async function probeHost(
  via: ProbeVia,
  pid: string,
  makeXhr?: XhrFactory,
): Promise<ProbeResult> {
  const base = `${PROBE_HOSTS[via]}/api/v1/miniapp/netprobe?via=${via}&pid=${pid}`;
  const s = await probeOnce(`${base}&size=s&t=${Date.now()}`, null, SMALL_TIMEOUT_MS, makeXhr);
  const l = await probeOnce(`${base}&size=l&t=${Date.now()}`, NETPROBE_LARGE_BYTES, LARGE_TIMEOUT_MS, makeXhr);
  return { via, s, l };
}

function newPid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* старый webview */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function dueNow(nowMs: number): boolean {
  try {
    const last = Number(localStorage.getItem(STORAGE_KEY) || '0');
    if (nowMs - last < EVERY_MS) return false;
    localStorage.setItem(STORAGE_KEY, String(nowMs)); // метка ДО пробы: сбой не зацикливает
    return true;
  } catch {
    return true; // хранилища нет — не чаще раза за открытие (зовут один раз)
  }
}

interface TelegramLike {
  WebApp?: { initData?: string; platform?: string; version?: string };
}

/**
 * Запустить пробу, если пора: только внутри Telegram (аудитория мини-апа), только с прод-
 * адреса, не чаще раза в сутки. Оба пути параллельно — один не ждёт таймаута другого.
 */
export async function runNetprobeIfDue(opts: {
  origin?: string;
  telegram?: TelegramLike;
  nowMs?: number;
  makeXhr?: XhrFactory;
  send?: (body: Record<string, unknown>) => void;
} = {}): Promise<ProbeResult[] | null> {
  try {
    const origin = opts.origin ?? window.location.origin;
    const entry = entryOf(origin);
    const tg = opts.telegram ?? (window as unknown as { Telegram?: TelegramLike }).Telegram;
    if (!entry || !tg?.WebApp?.initData) return null;
    if (!dueNow(opts.nowMs ?? Date.now())) return null;
    const pid = newPid();
    const results = await Promise.all([probeHost('ru', pid, opts.makeXhr), probeHost('nl', pid, opts.makeXhr)]);
    (opts.send ?? ((b) => sendRum(b)))({
      type: 'netprobe',
      pid,
      entry,
      platform: tg.WebApp?.platform,
      tgv: tg.WebApp?.version,
      results,
    });
    return results;
  } catch {
    return null; // проба никогда не мешает панели
  }
}
